import { useEffect, useRef, useState } from "react";
import { MinusSolidV6, PlusSolidV6 } from "../Icomoon";
import IconButton from "../IconButton";

const formatDisplayValue = (nextValue: number) => {
  if (!Number.isFinite(nextValue)) return "0";

  return nextValue.toLocaleString(undefined, {
    maximumFractionDigits: 20,
  });
};

const parseValue = (nextValue: string | number | readonly string[]) => {
  const parsedValue = Number(String(nextValue ?? "").replace(/,/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const NumberInput = ({
  className,
  onChange,
  value,
  inputProps = {},
  disableIncrement = false,
  disableDecrement = false,
  showControls = true,
  error,
}: {
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  disableIncrement?: boolean;
  disableDecrement?: boolean;
  showControls?: boolean;
  error?: boolean | string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputPropsOnChange = inputProps.onChange;
  const inputPropsOnKeyDown = inputProps.onKeyDown;
  const min = Number(inputProps.min || 0);
  const max = Number(inputProps.max || Number.POSITIVE_INFINITY);
  const rawStep = inputProps.step === "any" ? 1 : Number(inputProps.step ?? 1);
  const step = Number.isFinite(rawStep) && rawStep > 0 ? rawStep : 1;
  const [innerValue, setInnerValue] = useState(parseValue(value ?? inputProps.value ?? 0));

  const clamp = (nextValue: number) => Math.min(Math.max(nextValue, min), max);

  const updateValue = (nextValue: number) => {
    const clampedValue = clamp(nextValue);
    setInnerValue(clampedValue);

    if (inputRef.current) {
      inputRef.current.value = formatDisplayValue(clampedValue);
      // Create a synthetic event to pass to callbacks
      const syntheticEvent = {
        target: {
          ...inputRef.current,
          value: String(clampedValue),
        },
      } as React.ChangeEvent<HTMLInputElement>;
      inputPropsOnChange?.(syntheticEvent);
      onChange?.(syntheticEvent);
    }
  };

  const applyDelta = (delta: number) => {
    if (!inputRef.current) return;

    const rawCurrentValue = parseValue(inputRef.current.value);
    const currentValue = Number.isFinite(rawCurrentValue) ? rawCurrentValue : innerValue;
    updateValue(currentValue + delta);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseValue(e.target.value);
    setInnerValue(Number.isFinite(parsedValue) ? parsedValue : 0);

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: String(parsedValue),
      },
      currentTarget: {
        ...e.currentTarget,
        value: String(parsedValue),
      },
    } as React.ChangeEvent<HTMLInputElement>;

    inputPropsOnChange?.(syntheticEvent);
    onChange?.(syntheticEvent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    inputPropsOnKeyDown?.(e);
    if (e.defaultPrevented || inputProps.disabled) return;

    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        applyDelta(step);
        break;
      case "ArrowDown":
        e.preventDefault();
        applyDelta(-step);
        break;
      case "PageUp":
        e.preventDefault();
        applyDelta(step * 10);
        break;
      case "PageDown":
        e.preventDefault();
        applyDelta(-step * 10);
        break;
      case "Home":
        if (Number.isFinite(min)) {
          e.preventDefault();
          updateValue(min);
        }
        break;
      case "End":
        if (Number.isFinite(max)) {
          e.preventDefault();
          updateValue(max);
        }
        break;
      default:
    }
  };

  const hasAccessibleLabel = Boolean(inputProps["aria-label"] || inputProps["aria-labelledby"]);
  const displayValue = formatDisplayValue(innerValue);

  useEffect(() => {
    setInnerValue(parseValue(value ?? inputProps.value ?? 0));
  }, [value, inputProps.value]);

  return (
    <div className={`tw-inline-flex tw-items-center tw-gap-[8px] ${className}`}>
      {showControls && (
        // @ts-expect-error props
        <IconButton
          size="small"
          schema="secondary"
          disabled={inputProps.disabled || disableDecrement || innerValue <= min}
          aria-label="Decrease value"
          onClick={() => applyDelta(-step)}
        >
          <MinusSolidV6 />
        </IconButton>
      )}
      <input
        {...inputProps}
        className={`no-spinner tw-rounded-[4px] tw-border-solid tw-border-[1px] ${error ? "tw-border-semantic-red-100" : "tw-border-gray-30"} tw-bg-tinted-gray-50 tw-text-center ${showControls ? "tw-max-w-[48px]" : "tw-w-auto tw-text-right"} ${inputProps.className}`}
        type="text"
        inputMode={showControls ? "numeric" : "decimal"}
        size={typeof inputProps.size === "number" ? inputProps.size : 1}
        aria-label={hasAccessibleLabel ? inputProps["aria-label"] : "Number input"}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        ref={inputRef}
      />
      {showControls && (
        // @ts-expect-error props
        <IconButton
          size="small"
          schema="secondary"
          disabled={inputProps.disabled || disableIncrement || innerValue >= max}
          aria-label="Increase value"
          onClick={() => applyDelta(step)}
        >
          <PlusSolidV6 />
        </IconButton>
      )}
    </div>
  );
};

export default NumberInput;
